import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Phone, Image, Eye, EyeOff, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const RegisterPage = () => {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', mobile: '' });
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatar(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // basic front‑end validation
    if (!form.name || !form.email || !form.password || !form.confirmPassword || !form.mobile) {
      toast.error('يرجى ملء جميع الحقول');
      return;
    }
    if (!/^[A-Za-z\u0600-\u06FF ]+$/.test(form.name)) {
      toast.error('الاسم يجب أن يحتوي حروفاً فقط');
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('كلمة المرور وتأكيدها غير متطابقين');
      return;
    }
    if (!/(?=.*[A-Za-z])(?=.*\d).{6,}/.test(form.password)) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل وتحتوي على حروف وأرقام');
      return;
    }

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'confirmPassword') return; // backend doesn't need this
      fd.append(k, v);
    });
    if (avatar) fd.append('avatar', avatar);

    setLoading(true);
    try {
      await register(fd);
      toast.success('تم إنشاء الحساب بنجاح 🎉');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في التسجيل');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      background: 'var(--bg-base)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute', top: '20%', right: '10%',
        width: '400px', height: '400px',
        background: 'radial-gradient(circle, rgba(6,182,212,0.1) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />

      <div style={{ width: '100%', maxWidth: '460px', position: 'relative', zIndex: 1 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <Link to="/" style={{
            fontSize: '2rem', fontWeight: 900,
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            ظبطت 🎉
          </Link>
          <p style={{ color: 'var(--text-muted)', marginTop: '8px', fontSize: '0.9rem' }}>
            أنشئ حسابك وابدأ التنظيم
          </p>
        </div>

        <div className="card animate-slideUp" style={{ padding: '32px' }}>
          {/* Avatar Upload */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
            <label style={{ cursor: 'pointer', textAlign: 'center' }}>
              <input type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />
              <div style={{ position: 'relative', display: 'inline-block' }}>
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="avatar"
                    style={{
                      width: '80px', height: '80px', borderRadius: '50%',
                      objectFit: 'cover', border: '3px solid var(--primary)'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '80px', height: '80px', borderRadius: '50%',
                    background: 'var(--bg-elevated)',
                    border: '2px dashed var(--border-light)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    color: 'var(--text-muted)', gap: '4px'
                  }}>
                    <Image size={20} />
                    <span style={{ fontSize: '0.65rem' }}>صورتك</span>
                  </div>
                )}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0,
                  width: '24px', height: '24px', borderRadius: '50%',
                  background: 'var(--primary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Image size={12} color="white" />
                </div>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '6px' }}>
                اختياري
              </p>
            </label>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label"><User size={14} /> الاسم الكامل</label>
              <input
                type="text" className="form-input" placeholder="مثال: أحمد محمد"
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label"><Mail size={14} /> البريد الإلكتروني</label>
              <input
                type="email" className="form-input" placeholder="example@email.com"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                dir="ltr"
              />
            </div>

            <div className="form-group">
              <label className="form-label"><Phone size={14} /> رقم الموبايل</label>
              <input
                type="tel" className="form-input" placeholder="01012345678"
                value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })}
                dir="ltr"
              />
            </div>

            <div className="form-group">
              <label className="form-label"><Lock size={14} /> كلمة المرور</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'} className="form-input"
                  placeholder="6 أحرف على الأقل وحروف وأرقام"
                  value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  style={{ paddingLeft: '44px' }} dir="ltr"
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  style={{
                    position: 'absolute', left: '12px', top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none', border: 'none',
                    color: 'var(--text-muted)', cursor: 'pointer'
                  }}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label"><Lock size={14} /> تأكيد كلمة المرور</label>
              <input
                type={showPass ? 'text' : 'password'} className="form-input"
                placeholder="أعد كتابة كلمة المرور"
                value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                style={{ paddingLeft: '44px' }} dir="ltr"
              />
            </div>

            <button
              type="submit" className="btn btn-primary btn-full btn-lg"
              disabled={loading} style={{ marginTop: '8px' }}
            >
              {loading ? <div className="spinner spinner-sm" /> : <UserPlus size={18} />}
              {loading ? 'جاري الإنشاء...' : 'إنشاء الحساب'}
            </button>
          </form>

          <div className="divider" />

          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            عندك حساب؟{' '}
            <Link to="/login" style={{ color: 'var(--primary-light)', fontWeight: 600 }}>
              سجّل دخولك
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
